import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useCondoContext } from '@/features/auth/use-condo-context';
import {
  useResidente,
  useCerrarAsignacion,
  useDesactivarResidente,
} from '@/features/residentes/api';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { AsignarUnidadDialog } from '@/features/residentes/asignar-unidad-dialog';
import { formatLongDate } from '@/lib/format/date';

export function ResidenteDetailRoute() {
  const { residenteId } = useParams();
  const { condoId: tenantId } = useCondoContext();
  const { data, isLoading } = useResidente(tenantId, residenteId);

  const [asignarOpen, setAsignarOpen] = useState(false);
  const [cerrarOpen, setCerrarOpen] = useState(false);
  const [cerrarTarget, setCerrarTarget] = useState<string | null>(null);
  const [desactivarOpen, setDesactivarOpen] = useState(false);

  const cerrar = useCerrarAsignacion(
    tenantId,
    residenteId ?? '',
    cerrarTarget ?? '',
  );
  const desactivar = useDesactivarResidente(tenantId, residenteId ?? '');

  if (isLoading || !data) {
    return (
      <PageContainer width="wide">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-48" />
      </PageContainer>
    );
  }
  const r = data.residente;

  return (
    <PageContainer width="wide">
      <Button variant="ghost" asChild size="sm" className="mb-6">
        <Link to={`/app/condominios/${tenantId}/residentes`}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Link>
      </Button>
      <PageHeader
        eyebrow="Residente"
        title={`${r.nombre} ${r.apellidos}`}
        action={
          <div className="flex items-center gap-2">
            <Badge variant={r.activo ? 'success' : 'neutral'}>
              {r.activo ? 'Activo' : 'Inactivo'}
            </Badge>
            {r.activo && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDesactivarOpen(true)}
              >
                Desactivar
              </Button>
            )}
          </div>
        }
        meta={
          <p className="mt-2 font-mono text-sm text-[var(--color-ink-muted)]">
            {r.rut}
          </p>
        }
        ruleSpacing="mb-8"
      />

      <dl className="grid grid-cols-2 gap-6 text-sm">
        <div>
          <dt className="eyebrow">Correo</dt>
          <dd className="mt-1 text-[var(--color-ink)]">{r.email}</dd>
        </div>
        <div>
          <dt className="eyebrow">Telefono</dt>
          <dd className="mt-1 font-mono text-[var(--color-ink)]">
            {r.telefono ?? '—'}
          </dd>
        </div>
        <div>
          <dt className="eyebrow">Alta</dt>
          <dd className="mt-1 text-[var(--color-ink)]">
            {formatLongDate(r.createdAt)}
          </dd>
        </div>
      </dl>

      <div className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">Unidades vinculadas</h2>
          <Button size="sm" onClick={() => setAsignarOpen(true)}>
            + Asignar unidad
          </Button>
        </div>
        <ul className="mt-4 space-y-2">
          {data.asignaciones.length === 0 && (
            <li className="text-sm text-[var(--color-ink-muted)]">
              Sin unidades asignadas.
            </li>
          )}
          {data.asignaciones.map((a) => {
            const activa = a.fechaFin === null;
            return (
              <li
                key={`${a.unidadId}-${a.fechaInicio}`}
                className="flex items-center justify-between rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] px-4 py-3"
              >
                <div>
                  <p className="font-display text-sm font-semibold">
                    Unidad {a.unidadId.slice(-4)}
                  </p>
                  <p className="text-[0.6875rem] text-[var(--color-ink-muted)]">
                    Desde {formatLongDate(a.fechaInicio)}
                    {a.fechaFin ? ` → ${formatLongDate(a.fechaFin)}` : ''}
                    {' · '}
                    {a.habita ? 'habita' : 'no habita'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={a.rol === 'propietario' ? 'primary' : 'accent'}>
                    {a.rol}
                  </Badge>
                  {activa && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setCerrarTarget(a.unidadId);
                        setCerrarOpen(true);
                      }}
                    >
                      Cerrar
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <AsignarUnidadDialog
        open={asignarOpen}
        onOpenChange={setAsignarOpen}
        condoId={tenantId}
        residenteId={residenteId ?? ''}
      />

      <ConfirmDialog
        open={cerrarOpen}
        onOpenChange={(open) => {
          setCerrarOpen(open);
          if (!open) setCerrarTarget(null);
        }}
        title="Cerrar asignación"
        description="Se cerrará esta asignación con fecha de hoy. El residente dejará de estar vinculado a la unidad."
        variant="destructive"
        confirmLabel="Cerrar asignación"
        loading={cerrar.isPending}
        onConfirm={() => {
          const today = new Date().toISOString().slice(0, 10);
          cerrar.mutate(
            { fechaFin: today },
            {
              onSuccess: () => {
                setCerrarOpen(false);
                setCerrarTarget(null);
              },
            },
          );
        }}
      />

      <ConfirmDialog
        open={desactivarOpen}
        onOpenChange={setDesactivarOpen}
        title="Desactivar residente"
        description="El residente será marcado como inactivo. Requiere que no tenga asignaciones activas."
        variant="destructive"
        confirmLabel="Desactivar"
        loading={desactivar.isPending}
        onConfirm={() => {
          desactivar.mutate(undefined, {
            onSuccess: () => setDesactivarOpen(false),
          });
        }}
      />
    </PageContainer>
  );
}
